import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input } from '@angular/core';
import { SurfaceContextHandle } from '@workbuddy/ui/v2/surface.engine/core/runtime/surface.context/create.surface.context.options';

/**
 * Host for the QuickView surface — a drawer-style entity record shown in a board panel.
 *
 * Deliberately renders nothing yet. The surface type, its slots (summary + tabs) and its patch
 * semantics already exist in the definition; what was missing is a host, so a QuickView surface
 * mounted through `wb-surface` resolved to no component and rendered blank with no way to tell that
 * apart from a broken mount. This makes the mount real and observable.
 *
 * It is NOT a fork of the manage host: that one is a full-page layout (w-page, breadcrumbs, action
 * bar, banner region, summary sidebar, relation tabs) whose chrome is wrong inside a ~380px panel.
 * Sections and tabs get built here directly when this grows, against the same shared primitives
 * (`buildSectionRenderInfo`, `WbSectionComponent`, `WbTabShellComponent`) the manage host uses.
 */
@Component({
  selector: 'wb-surface-quick-view-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: '',
  host: {
    '[attr.data-surface]': '"quickView"',
    '[attr.data-entity]': '$entity()',
  },
})
export class WbSurfaceQuickViewHostComponent {

  public $handle = input.required<SurfaceContextHandle>({ alias: 'handle' });

  // Not rendered — exposed as a host attribute so a mounted-but-empty panel is visibly distinct
  // from a panel that failed to mount at all.
  protected readonly $entity = computed(() => this.$handle().compiled.entity ?? null);
}
